// ProcessDiagramElement.js
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    updateProcessDiagramPosition,
    updateProcessDiagramSize,
    updateProcessDiagramTitle,
} from '../../../store/actions'
import ProcessDiagramEdit from './ProcessDiagramEdit'
import { Server } from '../../../api/Server'
import { ElementWrapper } from '../components/ElementWrapper/ElementWrapper'
import { OBJECT_TYPES } from '../types'
import { togglePromptObject } from '../../../store/actions/promptActions'
import { LinkIcon, LinkBreakIcon } from '@phosphor-icons/react'
import { ElementInfo } from '../components/ElementInfo/ElementInfo'
import { ScoopMenu, ScoopMenuItem, ScoopTextField, ScoopButton } from '../../ui'
import { Process } from '../../screens/Insights/Process/Process'
import styles from './ProcessDiagramElement.module.scss'

const ProcessDiagramElement = ({
    id,
    title,
    initialPosition,
    initialSize,
    content,
    workspaceID,
    userID,
    token,
    multiProps,
    workspaceMetadata,
}) => {
    const processId = `ProcessDiagramElement-${id}`
    const [processDiagram, setProcessDiagram] = useState(null)
    const [server, setServer] = React.useState(new Server(workspaceID, userID, token))
    const dispatch = useDispatch()
    const [isProcessDiagramASkeleton, setIsProcessDiagramASkeleton] = React.useState(true) //when there is no ProcessDiagram data to display
    const [inputTitle, setInputTitle] = useState(title) // Local state for the input title
    const [isEditMode, setIsEditMode] = useState(false) // Local state for the edit mode
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const activeMode = useSelector((state) => state.ui.activeMode)
    const promptSelectingObjects = useSelector((state) => state.prompt.promptSelectingObjects)
    const promptSelecting = useSelector((state) => state.prompt.promptSelecting)
    const objects = useSelector((state) => state.objects)
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const guestPrompts = useSelector((state) => state.auth.guestPrompts)
    const updatedStyles = useSelector(
        (state) => state.objects.find((obj) => obj.id === id)?.wrapperStyles || {}
    )
    const chartTypeMenu = useRef()
    const [showChartTypeMenu, setShowChartTypeMenu] = useState(false)

    const getProcessPrompts = () => {
        let prompts
        if (isGuestMode && guestPrompts.length > 0) prompts = guestPrompts
        else prompts = objects.filter((obj) => obj.type === OBJECT_TYPES.PROMPT)
        return prompts
            .filter((prompt) => prompt.promptProps?.objects.includes(processId))
            .map((prompt) => prompt.promptProps.prompt)
            .filter((prompt) => prompt !== null)
    }

    const [editProcessDiagramOpen, setEditProcessDiagramOpen] = useState(false)
    const [wrapperSize, setWrapperSize] = useState(initialSize)

    useEffect(() => {
        if (content?.processDiagram) {
            setProcessDiagram(content.processDiagram)
            setInputTitle(content.processDiagram)
            setIsProcessDiagramASkeleton(false)
        }
    }, [content?.processDiagram])

    useEffect(() => {
        setInputTitle(title)
    }, [title])

    const handleTitleChange = (e) => {
        setInputTitle(e.target.value)
        dispatch(updateProcessDiagramTitle(id, e.target.value)) // Dispatch the UPDATE_SHEETLET_TITLE event
    }

    React.useEffect(() => {
        setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    const updateProcessDiagramPos = (position) => {
        dispatch(updateProcessDiagramPosition(id, position))
    }

    const updateProcessDiagramSz = (size) => {
        dispatch(updateProcessDiagramSize(id, size))
    }

    const promptSelected = promptSelectingObjects.includes(processId)

    return (
        <ElementWrapper
            id={`ProcessDiagramElement-${id}`}
            key={`ProcessDiagramElement-${id}`}
            setWrapperSizeTrack={setWrapperSize}
            editMode={isEditMode}
            toggleEditMode={setIsEditMode}
            initialPosition={initialPosition}
            initialSize={initialSize}
            updateElementSize={updateProcessDiagramSz}
            updateElementPosition={updateProcessDiagramPos}
            type={'process'}
            {...multiProps}
            objectId={id}
        >
            {promptSelecting && (
                <div className={styles.promptLinkButton}>
                    <ScoopButton
                        variant="text"
                        size="xxs"
                        onClick={() => dispatch(togglePromptObject(processId))}
                        className={
                            promptSelected
                                ? styles.promptLinkButtonActive
                                : styles.promptLinkButtonInactive
                        }
                    >
                        {promptSelected ? (
                            <LinkIcon size={20} weight="regular" />
                        ) : (
                            <LinkBreakIcon size={20} weight="regular" />
                        )}
                    </ScoopButton>
                </div>
            )}
            {isEditMode && (
                <>
                    <div className={styles.editMenu}>
                        <ProcessDiagramEdit
                            isMenuOpen={isMenuOpen}
                            setIsMenuOpen={setIsMenuOpen}
                            isProcessDiagramASkeleton={isProcessDiagramASkeleton}
                            setIsProcessDiagramASkeleton={setIsProcessDiagramASkeleton}
                            server={server}
                            title={title}
                            id={id}
                            processDiagram={processDiagram}
                            handleTitleChange={handleTitleChange}
                            editProcessDiagramOpen={editProcessDiagramOpen}
                            setEditProcessDiagramOpen={setEditProcessDiagramOpen}
                            workspaceMetadata={workspaceMetadata}
                        />
                    </div>
                    <ElementInfo id={id} type={'Diagram'} title={title ?? 'Untitled'} />
                </>
            )}
            {updatedStyles.showTitle && (
                <div className={styles.titleWrapper}>
                    <ScoopTextField
                        className={styles.titleInput}
                        placeholder={title}
                        value={inputTitle}
                        onChange={handleTitleChange}
                    />
                </div>
            )}
            {isProcessDiagramASkeleton && <div className={styles.skeletonPlaceholder} />}
            {!isProcessDiagramASkeleton && (
                <div
                    className={`${styles.contentWrapper} ${
                        activeMode === 'edit'
                            ? styles.contentWrapperEditMode
                            : styles.contentWrapperViewMode
                    }`}
                >
                    <Process
                        activePrompts={getProcessPrompts()}
                        embeddedDiagram={{
                            embedded: true,
                            processDiagram: processDiagram,
                            left: 0,
                            width: wrapperSize.width,
                            top: 0,
                            height: wrapperSize.height,
                            clickable: activeMode !== 'edit',
                            workspaceMetadata: workspaceMetadata,
                        }}
                    />
                </div>
            )}
            <ScoopMenu
                open={showChartTypeMenu}
                anchorEl={chartTypeMenu.current}
                onClose={() => setShowChartTypeMenu(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <ScoopMenuItem
                    primary="Sankey Chart"
                    onClick={(e) => {
                        e.stopPropagation()
                        setShowChartTypeMenu(false)
                    }}
                />
                <ScoopMenuItem
                    primary="Process Diagram"
                    onClick={(e) => {
                        e.stopPropagation()
                        setShowChartTypeMenu(false)
                    }}
                />
            </ScoopMenu>
        </ElementWrapper>
    )
}

export default ProcessDiagramElement
